import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Client } from '../../../models/Client';
import { Contact } from '../../../models/Contact';
import { Channel } from '../../../enums/channel';
import { Practice } from '../../../models/Practice';
import { UntypedFormBuilder } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../state/reducers';
import {
  getCurrentPractice,
  getCurrentPracticeSites,
} from '../../../practices/state/selectors';
import { takeWhile } from 'rxjs/operators';
import {
  CreateFormSubmission,
  GetFormToSend,
  GetMoreSendFormPatients,
  SearchForms,
  SetFormRequestClient,
  SetFormRequestContact,
} from '../../state/actions';
import {
  getCreateFormSubmissionInProgress,
  getCurrentFormToSend,
  getFormRequestChannel,
  getFormRequestClient,
  getFormRequestContact,
  getFormRequestPatients,
  IsFormRequestPatientsLoading,
} from "../../state/selectors";
import { Form } from "../../interfaces/form";
import { PageField } from "../../interfaces/page-field";
import { fieldsContainPatientInformation } from "../../../helpers/fields-contain-patient-information";
import { Patient } from "../../../models/Patient";
import { phone } from "phone";
import { getClients } from "../../../clients/state/selectors";
import { GetClientSearchResults } from "../../../clients/state/actions";
import { getUser } from "../../../auth/state/selectors";
import { User } from "../../../models/User";
import { Site } from "../../../models/Site";
import { FormSubmissionPreFilledField } from "../../interfaces/form-submission-pre-filled-field";
import { PMS } from "../../../enums/pms";
import { getConversation } from '../../../conversation/state/selectors';
import { FormRequestSteps } from '../../../enums/form-request-steps';
import { TranslateService } from '@ngx-translate/core';
import { getPermittedMessagingChannels } from '../../../helpers/get-permitted-messaging-channels';
import { ChannelOption } from '../../../interfaces/channel-option.interface';
import { Currency } from '../../../models/Currency';
import { getCurrencies } from '../../../state/selectors';
import { FormSubmissionDataCreateDto } from '../../interfaces/dto/form-submission-data-create.dto';
import { getFieldPrefillValue } from '../../../helpers/get-field-prefill-value';

@Component({
  selector: 'new-form-request',
  templateUrl: './new-form-request.component.html',
  styleUrls: ['./new-form-request.component.scss'],
})
export class NewFormRequestComponent implements OnInit, OnDestroy {
  @ViewChildren('prefillInputs') prefillInputs:
    | QueryList<ElementRef>
    | undefined;
  formsubmissionPreFilledFields: Array<FormSubmissionPreFilledField> = [];
  alive = true;
  step = 0;
  createInProgress = false;
  selectedClient: Client | null = null;
  selectedContact: Contact | null = null;
  patients: Patient[] = [];
  selectedPatient: Patient | null = null;
  presetChannel: Channel | null = null;
  practice: Practice | null = null;
  channels: ChannelOption[] = [];
  selectedChannel?: ChannelOption;
  currentChannelOption: ChannelOption = this.channels[0];
  selectedForm: Form | null = null;
  fullForm: Form | null = null;
  fieldsToPrefill: PageField[] = [];
  formLoading = false;
  patientsLoading = false;
  patientsPage = 0;
  phoneNumber = '';
  phoneNumberIsValid = false;
  clients$ = this.store.pipe(select(getClients));
  user?: User;
  sites: Site[] = [];
  selectedSiteId = 0;
  siteError: string | null = null;
  isRegistered = false;
  showNoClientWarning = false;
  errors: { key: string; params: any }[] = [];
  paymentAmountError: string | boolean | null = null;
  paymentDescriptionError: string | boolean | null = null;
  maxPaymentDescriptionLength = 100;
  practiceCurrency?: Currency;
  currencies: Currency[] = [];
  formValues: { [key: string]: any } = {};

  constructor(
    private store: Store<AppState>,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.subscribeToCurrentUser();
    this.subscribeToCurrentPractice();
    this.subscribeToPracticeSites();
    this.subscribeToClient();
    this.subscribeToContact();
    this.subscribeToChannel();
    this.subscribeToCurrentForm();
    this.subscribeToPatientsLoading();
    this.subscribeToPatients();
    this.subscribeToCreateFormSubmissionInProgress();
    this.isRegisteredClient();
    this.subscribeToCurrencies();
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  subscribeToCurrencies(): void {
    this.store
      .pipe(select(getCurrencies))
      .pipe(takeWhile(() => this.alive))
      .subscribe((currencies) => {
        this.currencies = currencies;
        this.setPracticeCurrency();
      });
  }

  setPracticeCurrency(): void {
    if (this.currencies.length && this.practice) {
      this.practiceCurrency = this.currencies.find(
        (currency) => currency.currencyCode === this.practice?.currency
      );
    }
  }

  subscribeToCurrentPractice(): void {
    this.store
      .pipe(select(getCurrentPractice))
      .pipe(takeWhile(() => this.alive))
      .subscribe((practice) => {
        this.practice = practice;
        this.setPracticeCurrency();

        if (practice) {
          this.channels = getPermittedMessagingChannels(practice);
          this.currentChannelOption = this.channels[0];
        }
      });
  }

  subscribeToCurrentUser(): void {
    this.store
      .pipe(select(getUser))
      .pipe(takeWhile(() => this.alive))
      .subscribe((user) => {
        if (user) {
          this.user = user;
        }
      });
  }

  subscribeToPracticeSites(): void {
    this.store
      .select(getCurrentPracticeSites)
      .pipe(takeWhile(() => this.alive))
      .subscribe((sites) => {
        this.sites = sites;
      });
  }

  subscribeToPatientsLoading(): void {
    this.store
      .pipe(select(IsFormRequestPatientsLoading))
      .pipe(takeWhile(() => this.alive))
      .subscribe((loading) => {
        this.patientsLoading = loading;
      });
  }

  subscribeToCreateFormSubmissionInProgress(): void {
    this.store
      .pipe(select(getCreateFormSubmissionInProgress))
      .pipe(takeWhile(() => this.alive))
      .subscribe((inProgress) => {
        this.createInProgress = inProgress;
      });
  }

  subscribeToPatients(): void {
    this.store
      .pipe(select(getFormRequestPatients))
      .pipe(takeWhile(() => this.alive))
      .subscribe((patients) => {
        this.patients = patients;
      });
  }

  subscribeToClient(): void {
    this.store
      .pipe(select(getFormRequestClient))
      .pipe(takeWhile(() => this.alive))
      .subscribe((client) => {
        this.selectedClient = client;
        this.phoneNumber = '';
        this.showNoClientWarning = false;
        this.goToNextStep();
      });

    if (!this.selectedClient) {
      this.store
        .pipe(select(getConversation))
        .pipe(takeWhile(() => this.alive))
        .subscribe((conversation) => {
          if (conversation && !conversation.client) {
            const externalUser = conversation.users
              ? conversation.users.find((user) => user.channel !== Channel.INTERNAL)
              : null;
            if (externalUser && externalUser.phone) {
              this.phoneNumber = externalUser.phone;
              this.showNoClientWarning = true;
              this.validatePhoneInput();
            }
          }

        })
    }
  }

  hideNoClientWarning() {
    this.showNoClientWarning = false;
  }

  subscribeToContact(): void {
    this.store
      .pipe(select(getFormRequestContact))
      .pipe(takeWhile(() => this.alive))
      .subscribe((contact) => {
        this.selectedContact = contact;
        this.goToNextStep();
      });
  }

  subscribeToChannel(): void {
    this.store
      .pipe(select(getFormRequestChannel))
      .pipe(takeWhile(() => this.alive))
      .subscribe((channel) => {
        this.presetChannel = channel;
        this.goToNextStep();
      });
  }

  subscribeToCurrentForm(): void {
    this.store
      .pipe(select(getCurrentFormToSend))
      .pipe(takeWhile(() => this.alive))
      .subscribe((form) => {
        this.fieldsToPrefill = [];
        this.fullForm = form;

        if (form) {
          form.formPages?.forEach((page) => {
            page.pageFields
              ?.filter((field) => field.prefill || field.customizeContent)
              .forEach((prefillField) => {
                this.fieldsToPrefill.push(prefillField);
              });
          });

          this.formLoading = false;
          this.initializeFormValues();

          this.goToNextStep();
        }
      });
  }

  initializeFormValues(): void {
    this.fieldsToPrefill.forEach(field => {
      const key = field.id.toString();
      let initialValue = this.formValues[key] || field.default || '';

      if (field.prefill && field.prefillWith && this.selectedClient && !field.customizeContent) {
        initialValue = getFieldPrefillValue(
          field.prefillWith,
          this.selectedClient,
          this.selectedPatient,
          this.practice
        );
      }

      this.formValues[key] = initialValue;
    });

    if (this.selectedForm) {
      const amountKey = `payment_${this.selectedForm.id}_amount`;
      const descKey = `payment_${this.selectedForm.id}_description`;

      if (!this.formValues[amountKey]) {
        this.formValues[amountKey] = this.selectedForm.paymentAmount || 0;
      }
      if (!this.formValues[descKey]) {
        this.formValues[descKey] = this.selectedForm.paymentDescription || '';
      }
    }
  }

  getFieldValue(fieldId: number | string): any {
    return this.formValues[fieldId.toString()] || '';
  }

  updateFieldValue(fieldId: number | string, value: any): void {
    this.formValues[fieldId.toString()] = value;
  }

  isRegisteredClient(): void {
    if (this.selectedClient !== null) {
      this.selectClient(this.selectedClient);
      this.showNoClientWarning = false;
    }
  }

  selectClient(client: Client): void {
    this.store.dispatch(SetFormRequestClient({ client }));
    this.isRegistered = true;
  }

  selectContact(contact: Contact): void {
    this.store.dispatch(SetFormRequestContact({ contact }));
  }

  private showPatientSelector(): boolean {
    if (!this.selectedClient) return false;
    
    // For Teleos practices, always show patient selector
    if (this.practice && this.practice.pms === PMS.TELEOS) {
      return true;
    }
    
    const hasPatientSelectorInPrefill = fieldsContainPatientInformation(this.fieldsToPrefill);
    if (this.selectedForm?.saveAgainst === 'patient' || hasPatientSelectorInPrefill) {
      return true;
    }
    
    return false;
  }

  validatePaymentFields(): boolean {
    if (!this.selectedForm) return false;
    this.errors = [];
    this.paymentAmountError = null;
    this.paymentDescriptionError = null;

    const practiceCurrency = this.practiceCurrency;
    if (!practiceCurrency) {
      return false;
    }

    let valid = true;
    const amountKey = `payment_${this.selectedForm.id}_amount`;
    const descKey = `payment_${this.selectedForm.id}_description`;

    const amountValue = this.formValues[amountKey];
    const descValue = this.formValues[descKey];

    const userAmount = parseFloat(amountValue);
    if (!amountValue || isNaN(userAmount)) {
      this.errors.push({
        key: 'ui.payment_request_form.errors.amount_required',
        params: {}
      });
      this.paymentAmountError = this.translateService.instant(
        'ui.payment_request_form.errors.amount_required'
      );
      valid = false;
    } else if (userAmount < practiceCurrency.minimumCharge) {
      this.errors.push({
        key: 'ui.payment_request_form.errors.amount_min',
        params: {
          currency: practiceCurrency.currencySymbol,
          minimum: practiceCurrency.minimumCharge
        }
      });
      this.paymentAmountError = this.translateService.instant(
        'ui.payment_request_form.errors.amount_min',
        {
          currency: practiceCurrency.currencySymbol,
          minimum: practiceCurrency.minimumCharge
        }
      );
      valid = false;
    } else if (userAmount > 999999.99) {
      this.errors.push({
        key: 'ui.payment_request_form.errors.amount_max',
        params: {
          currency: practiceCurrency.currencySymbol,
        }
      });
      this.paymentAmountError = this.translateService.instant(
        'ui.payment_request_form.errors.amount_max', {
          currency: practiceCurrency.currencySymbol,
        }
      );
      valid = false;
    }

    // Validate description
    if (!descValue || !descValue.toString().trim()) {
      this.errors.push({
        key: 'ui.payment_request_form.errors.description_required',
        params: {}
      });
      this.paymentDescriptionError = this.translateService.instant('ui.payment_request_form.errors.description_required');
      valid = false;
    } else if (descValue.toString().length > this.maxPaymentDescriptionLength) {
      this.errors.push({
        key: 'ui.payment_request_form.errors.description_length',
        params: { length: this.maxPaymentDescriptionLength.toString() }
      });
      this.paymentDescriptionError = this.translateService.instant('ui.payment_request_form.errors.description_length', {
        length: this.maxPaymentDescriptionLength
      });
      valid = false;
    }

    return valid;
  }

  goToNextStep(): void {
    let step = this.determineNextStep();
    this.handleAutoActions(step);
    this.step = step;
  }

  private determineNextStep(): number {
    // Handle phone number flow first if applicable
    if (this.isPhoneNumberFlow()) {
      return this.determinePhoneNumberFlowStep();
    }

    // Handle normal client flow
    return this.determineClientFlowStep();
  }

  private isPhoneNumberFlow(): boolean {
    return !!(this.phoneNumber && this.selectedForm && this.practice);
  }

  private determinePhoneNumberFlowStep(): number {
    if (this.shouldShowPrefillFields()) {
      return FormRequestSteps.PREFILL_FIELDS;
    } else if (this.shouldShowPaymentStep() && this.shouldShowSiteSelection()) {
      return FormRequestSteps.UPDATE_PAYMENT_INFO;
    } else if (!this.shouldShowPaymentStep() && this.shouldShowSiteSelection()) {
      return FormRequestSteps.SELECT_SITE;
    }
    // Auto-submit case handled in handleAutoActions
    return FormRequestSteps.SELECT_FORM;
  }

  private determineClientFlowStep(): number {
    // Basic progression through required steps
    if (!this.selectedClient) return FormRequestSteps.SEARCH_CLIENT;
    if (!this.selectedContact) return FormRequestSteps.SELECT_CONTACT;
    if (!this.presetChannel && !this.selectedChannel) return FormRequestSteps.SELECT_CHANNEL;
    if (!this.selectedForm) return FormRequestSteps.SELECT_FORM;

    // Patient selection logic
    if (this.shouldSelectPatient()) {
      return FormRequestSteps.SELECT_PATIENT;
    }

    // Determine final steps based on conditions
    return this.determineFinalStep();
  }

  private shouldSelectPatient(): boolean {
    const needsPatientSelector = this.showPatientSelector();
    const hasPatient = !!this.selectedPatient;

    if (!needsPatientSelector) return false;
    if (hasPatient) return false;

    // For complex patient logic - maintain exact original behavior
    if (this.selectedClient && this.selectedContact &&
        (this.presetChannel || this.selectedChannel) && this.selectedForm) {

      if (this.practice && this.practice.pms === PMS.TELEOS) {
        return true; // Always need patient selection for Teleos
      }

      // Check if we should skip patient selection for non-Teleos practices
      const canSkipPatientForNonTeleos =
        this.practice &&
        !this.formLoading &&
        !fieldsContainPatientInformation(this.fieldsToPrefill);

      if (canSkipPatientForNonTeleos) {
        return false; // Skip patient selection
      }

      return true; // Need patient selection
    }

    return needsPatientSelector && !hasPatient;
  }

  private determineFinalStep(): number {
    // Priority order: Prefill → Payment → Site Selection → Auto-submit
    if (this.shouldShowPrefillFields()) {
      return FormRequestSteps.PREFILL_FIELDS;
    }

    if (this.shouldShowPaymentStep()) {
      return FormRequestSteps.UPDATE_PAYMENT_INFO;
    }

    if (this.shouldShowSiteSelection()) {
      return FormRequestSteps.SELECT_SITE;
    }

    // Auto-submit case handled in handleAutoActions
    return FormRequestSteps.SELECT_FORM;
  }

  private shouldShowPrefillFields(): boolean {
    return this.fieldsToPrefill.length > 0;
  }

  private shouldShowPaymentStep(): boolean {
    return this.selectedForm?.takePayment === true;
  }

  private shouldShowSiteSelection(): boolean {
    return this.sites.length > 0;
  }

  private handleAutoActions(step: number): void {
    // Auto-set single site for prefill fields step
    if (step === FormRequestSteps.PREFILL_FIELDS && this.sites.length === 1) {
      this.selectedSiteId = Number(this.sites[0].id);
    }

    // Auto-set single site for payment step
    if (step === FormRequestSteps.UPDATE_PAYMENT_INFO && this.sites.length === 1 && this.selectedForm?.takePayment) {
      this.selectedSiteId = Number(this.sites[0].id);
    }

    // Auto-submit scenarios
    if (this.shouldAutoSubmit(step)) {
      this.autoSubmitForm(step);
    }
  }

  private shouldAutoSubmit(step: number): boolean {
    // Auto-submit if we reach site selection with one or zero sites
    if (step === FormRequestSteps.SELECT_SITE && this.sites.length <= 1) {
      return true;
    }

    // Auto-submit if we're at select form step (no additional steps needed)
    if (step === FormRequestSteps.SELECT_FORM) {
      return true;
    }

    // Auto-submit for phone number flow
    if (this.phoneNumber && this.selectedForm && this.practice) {
      return true;
    }

    return false;
  }

  private autoSubmitForm(step: number): void {
    // Set site ID for auto-submit cases
    if (step === FormRequestSteps.SELECT_SITE && this.sites.length === 1) {
      this.selectedSiteId = Number(this.sites[0].id);
    } else {
      this.selectedSiteId = 0;
    }

    this.sendForm();
  }

  goBackTo(step: number): void {
    if (step === 0) {
      this.store.dispatch(SetFormRequestClient({}));
    }

    if (step === 1) {
      if (!this.selectedClient) {
        this.selectedContact = null;
        this.store.dispatch(SetFormRequestContact({}));
        this.step = 0;   // ← BACK TO ORIGINAL INPUT SCREEN
        return;
      }
      // 🔹 If client exists → normal behavior
      this.selectedContact = null;
      this.store.dispatch(SetFormRequestContact({}));
      this.step = 1;
      return;
    }

    if (step === 2) {
      if (this.presetChannel && this.selectedChannel) {
        // go back past channel step if channel is preset
        this.goBackTo(1);
      } else {
        this.selectedChannel = undefined;
        this.step = 2;
      }
    }

    if (step === 3) {
      this.selectedForm = null;
      this.fullForm = null;
      this.step = 3;
    }

    if (step === 4) {
      this.selectedPatient = null;

      if (!this.selectedClient) {
        this.step = 3;
        return;
      }

      if (!this.showPatientSelector()) {
        this.goBackTo(3);
      } else {
        this.step = 4;
      }
    }

    if (step === 5) {
      if (this.fieldsToPrefill.length === 0) {
        this.goBackTo(4);
      } else {
        this.step = 5;
      }
    }

    if (step === 6) {
      if (this.selectedForm) {
        this.goBackTo(6);
      }
    }
  }

  goToFormStep(): void {
    this.selectedChannel = this.currentChannelOption;
    this.store.dispatch(SearchForms({ search: '' }));
    this.step = 3;
  }

  handleFormSelected(form: Form): void {
    this.selectedForm = form;
    this.getFullForm();
  }

  getFullForm(): void {
    if (this.selectedForm) {
      this.formLoading = true;
      this.store.dispatch(GetFormToSend({ formId: this.selectedForm.id }));
    }
  }

  sendForm(): void {
    if (this.selectedForm?.takePayment && !this.validatePaymentFields()) {
      return;
    }

    this.siteError = null;

    this.setPrefilledForm();

    if (!this.selectedForm || !this.practice) {
      return;
    }

    const selectedSiteIdNumber = Number(this.selectedSiteId);

    if (selectedSiteIdNumber === 0 && this.sites.length > 0) {
      this.siteError = this.translateService.instant('forms.components.new_request.site_error');
      return;
    }

    const baseDto = {
      formId: this.selectedForm!.id.toString(),
      practiceId: this.practice!.coreId,
      ownerId: this.user?.id || '0',
      patientPmsId: this.selectedPatient?.pmsId || null,
      vbResourceId: this.getSelectedSiteVbResourceId(selectedSiteIdNumber),
      practiceCountry: this.practice!.country,
      data: this.mapPrefilledFieldsToSubmissionData(),
    };

    if (!!(this.selectedClient && this.selectedContact)) {
      this.submitWithClient(baseDto);
    } else if (!!(this.phoneNumber && this.selectedForm && this.practice)) {
      this.submitWithPhoneNumber(baseDto);
    }
  }

  private getSelectedSiteVbResourceId(selectedSiteIdNumber: number): string | null {
    const site = this.sites.find(site => site.id.toString() === selectedSiteIdNumber.toString());
    return site?.vetbookerResourceId || null;
  }

  private mapPrefilledFieldsToSubmissionData(): FormSubmissionDataCreateDto[] {
    return this.formsubmissionPreFilledFields.length
      ? this.formsubmissionPreFilledFields.map(field => {
          const matchingPrefill = this.fieldsToPrefill.find(
            prefillField => prefillField.id.toString() === field.fieldId.toString()
          );
          return {
            fieldId: field.fieldId,
            fieldLabel: matchingPrefill?.label || '',
            fieldValue: field.fieldValue,
          };
        })
      : [];
  }

  private submitWithClient(baseDto: any): void {
    this.store.dispatch(
      CreateFormSubmission({
        dto: {
          ...baseDto,
          clientPmsId: this.selectedClient!.pmsId ?? this.selectedClient!.pmsDisplayId,
          contactNumber: this.selectedContact!.value,
        },
        client: this.selectedClient!,
        contact: this.selectedContact!,
        channel: this.getSelectedChannel(),
      })
    );
  }

  private submitWithPhoneNumber(baseDto: any): void {
    this.store.dispatch(
      CreateFormSubmission({
        dto: {
          ...baseDto,
          clientPmsId: null,
          contactNumber: this.phoneNumber,
        },
        client: null,
        contact: {
          name: 'Mobile',
          typeCode: '0',
          value: this.phoneNumber,
          facebookLinked: false,
          instagramLinked: false,
        },
        channel: this.getSelectedChannel(),
      })
    );
  }

  private getSelectedChannel(): Channel {
    return this.presetChannel || this.selectedChannel?.code || Channel.WHATSAPP360;
  }

  onSiteIdChange(newSiteId: string): void {
    this.siteError = null;
  }

  selectPatient(patient: Patient): void {
    this.selectedPatient = patient;
    this.initializeFormValues();
    this.goToNextStep();
  }

  handleMorePatients(): void {
    this.patientsPage++;
    this.store.dispatch(GetMoreSendFormPatients({ page: this.patientsPage }));
  }

  validatePhoneInput(): void {
    let phoneResult = phone(this.phoneNumber);
    if (!phoneResult.isValid) {
      phoneResult = phone(this.phoneNumber, {
        country: this.practice?.country,
      });
    }

    this.phoneNumberIsValid = !!phoneResult.isValid;
  }

  validateKeyPress($event: KeyboardEvent): void {
    const regex = new RegExp('^[a-zA-Z]+$');
    const key = $event.key;

    if (
      key.length === 1 &&
      !$event.ctrlKey &&
      !$event.metaKey &&
      regex.test(key)
    ) {
      $event.preventDefault();
    }
  }

  usePhoneNumber(): void {
    this.showNoClientWarning = true;
    let phoneResult = phone(this.phoneNumber, {
      country: this.practice?.country,
    });
    if (!phoneResult.isValid) {
      phoneResult = phone(this.phoneNumber);
    }
    if (!phoneResult.isValid || !phoneResult.phoneNumber) {
      this.phoneNumberIsValid = false;
      return;
    }
    this.goBackTo(2);
  }

  setPrefilledForm(): void {
    this.formsubmissionPreFilledFields = [];
  
    this.fieldsToPrefill.forEach(field => {
      let fieldValue = this.getFieldValue(field.id);
      
      // If no stored value, try to get the prefilled value for prefill fields only
      if ((fieldValue === undefined || fieldValue === null || fieldValue === '') && field.prefill && field.prefillWith && !field.customizeContent) {
        if (this.selectedClient) {
          fieldValue = getFieldPrefillValue(field.prefillWith, this.selectedClient, this.selectedPatient, this.practice);
        }
      }
      
      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
        this.formsubmissionPreFilledFields.push({
          fieldId: field.id,
          fieldValue: fieldValue,
        });
      }
    });

    if (this.selectedForm?.takePayment) {
      const amountKey = `payment_${this.selectedForm.id}_amount`;
      const descKey = `payment_${this.selectedForm.id}_description`;
      const paymentAmount = this.getFieldValue(amountKey);
      const paymentDescription = this.getFieldValue(descKey);
      
      if (paymentAmount !== undefined && paymentAmount !== null && paymentAmount !== '') {
        this.formsubmissionPreFilledFields.push({
          fieldId: this.selectedForm.id,
          fieldValue: paymentAmount.toString(),
        });
      }
      
      if (paymentDescription !== undefined && paymentDescription !== null && paymentDescription !== '') {
        this.formsubmissionPreFilledFields.push({
          fieldId: this.selectedForm.id,
          fieldValue: paymentDescription.toString(),
        });
      }
    }
  }

  goToSiteStep(): void {
    if (this.selectedForm?.takePayment && !this.validatePaymentFields()) {
      return;
    }

    this.setPrefilledForm();
    if (this.sites.length > 0 && this.sites.length === 1) {
      this.selectedSiteId = Number(this.sites[0].id);
      this.sendForm();
    }
    this.step = FormRequestSteps.SELECT_SITE;
  }

  goToPaymentStep(): void {
    this.setPrefilledForm();
    this.step = FormRequestSteps.UPDATE_PAYMENT_INFO;

    if (this.selectedForm?.takePayment) {
      setTimeout(() => {
        this.validatePaymentFields();
      }, 100);
    }
  }

  isPaymentFormValid(): boolean {
    return !this.selectedForm?.takePayment || this.errors.length === 0;
  }

  updatePaymentAmount(value: any): void {
    if (this.selectedForm) {
      const key = `payment_${this.selectedForm.id}_amount`;
      this.updateFieldValue(key, value);
      this.selectedForm.paymentAmount = parseFloat(value) || 0;
    }
  }

  updatePaymentDescription(value: any): void {
    if (this.selectedForm) {
      const key = `payment_${this.selectedForm.id}_description`;
      this.updateFieldValue(key, value);
      this.selectedForm.paymentDescription = value;
    }
  }
}
